import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { CollapsibleSection } from "./CollapsibleSection";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
  listStudentGuardians, addStudentGuardian,
  removeStudentGuardian, resetStudentGuardianPassword,
  getParentSettings, updateParentSettings, updatePickupAlerts, notifyProgressReport,
  getParentEditHistory,
} from "../../lib/api";

// Map a Resend delivery event to a small status badge.
function DeliveryBadge({ guardian }) {
  if (!guardian.has_login || !guardian.invite_email_id) {
    return (
      <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500" data-testid={`guardian-delivery-${guardian.email}`}>
        No invite sent
      </span>
    );
  }
  const s = (guardian.delivery_status || "").toLowerCase();
  const map = {
    delivered: { label: "Delivered \u2713", cls: "bg-green-100 text-green-700" },
    bounced: { label: "Bounced", cls: "bg-red-100 text-red-700" },
    complained: { label: "Marked spam", cls: "bg-red-100 text-red-700" },
    suppressed: { label: "Suppressed", cls: "bg-orange-100 text-orange-700" },
    delivery_delayed: { label: "Delayed", cls: "bg-amber-100 text-amber-700" },
    sent: { label: "Sent \u00b7 pending", cls: "bg-slate-100 text-slate-600" },
  };
  const badge = map[s] || { label: s ? `Sent \u00b7 ${s}` : "Sent \u00b7 pending", cls: "bg-slate-100 text-slate-600" };
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full ${badge.cls}`} title={`Email delivery status: ${s || "pending"}`} data-testid={`guardian-delivery-${guardian.email}`}>
      {badge.label}
    </span>
  );
}

export function GuardianAccessCard({ studentId }) {
  const [guardians, setGuardians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [absenceAlerts, setAbsenceAlerts] = useState(true);
  const [pickupAlerts, setPickupAlerts] = useState(true);
  const [notifying, setNotifying] = useState(false);
  const [history, setHistory] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [gRes, sRes, hRes] = await Promise.all([
        listStudentGuardians(studentId),
        getParentSettings(studentId),
        getParentEditHistory(studentId),
      ]);
      setGuardians(gRes.data || []);
      setAbsenceAlerts(sRes.data?.absence_alerts_enabled !== false);
      setPickupAlerts(sRes.data?.pickup_alerts_enabled !== false);
      setHistory(hRes.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load guardians");
    } finally { setLoading(false); }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!email.trim()) { toast.error("Enter a guardian email"); return; }
    setSaving(true);
    try {
      const res = await addStudentGuardian(studentId, { email: email.trim(), name: name.trim() });
      if (res.data.temp_password) {
        toast.success(`Guardian added. Temp password: ${res.data.temp_password} (also emailed)`, { duration: 10000 });
      } else {
        toast.success(res.data.message || "Guardian added");
      }
      setEmail(""); setName("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add guardian");
    } finally { setSaving(false); }
  };

  const remove = async (gEmail) => {
    if (!window.confirm(`Remove ${gEmail}'s access to this student?`)) return;
    try {
      await removeStudentGuardian(studentId, gEmail);
      toast.success("Guardian removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove guardian");
    }
  };

  const reset = async (gEmail) => {
    try {
      const res = await resetStudentGuardianPassword(studentId, gEmail);
      toast.success(`New temp password: ${res.data.temp_password} (also emailed)`, { duration: 10000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reset password");
    }
  };

  const resendWelcome = async (gEmail) => {
    if (!window.confirm(`Resend the welcome email to ${gEmail}? This generates a fresh temporary password.`)) return;
    try {
      const res = await resetStudentGuardianPassword(studentId, gEmail);
      toast.success(`Welcome email resent to ${gEmail}. Temp password: ${res.data.temp_password}`, { duration: 10000 });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to resend welcome email");
    }
  };

  const toggleAbsence = async (val) => {
    setAbsenceAlerts(val);
    try {
      await updateParentSettings(studentId, val);
      toast.success(val ? "Absence alerts enabled" : "Absence alerts disabled");
    } catch (e) {
      setAbsenceAlerts(!val);
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const togglePickup = async (val) => {
    setPickupAlerts(val);
    try {
      await updatePickupAlerts(studentId, val);
      toast.success(val ? "Pickup-time alerts enabled" : "Pickup-time alerts disabled");
    } catch (e) {
      setPickupAlerts(!val);
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const sendProgressReport = async () => {
    setNotifying(true);
    try {
      const res = await notifyProgressReport(studentId);
      toast.success(res.data.message || "Notification sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send notification");
    } finally { setNotifying(false); }
  };

  return (
    <>
    <CollapsibleSection
      id="parent-emails"
      title="Parent / Guardian Emails"
      color="sky"
      testid="guardian-access-card"
      right={
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="guardian-refresh-btn" className="bg-white/90 text-sky-700 hover:bg-white border-white/60">
          {loading ? "Refreshing…" : "Refresh status"}
        </Button>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Give a parent/guardian read-only access to this student&apos;s demographics, attendance and assessments.
          Each email logs in on the main login page; a temporary password is emailed on first add.
        </p>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
          <div className="flex-1 space-y-1">
            <Label className="text-xs">Guardian Email</Label>
            <Input type="email" value={email} placeholder="parent@example.com" onChange={(e) => setEmail(e.target.value)} data-testid="guardian-email-input" />
          </div>
          <div className="flex-1 space-y-1">
            <Label className="text-xs">Guardian Name (optional)</Label>
            <Input value={name} placeholder="e.g. Jane Doe" onChange={(e) => setName(e.target.value)} data-testid="guardian-name-input" />
          </div>
          <Button onClick={add} disabled={saving} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="guardian-add-btn">
            {saving ? "Adding..." : "Add Guardian"}
          </Button>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground py-2">Loading…</p>
        ) : guardians.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2" data-testid="guardian-empty">No guardian emails added yet.</p>
        ) : (
          <div className="divide-y border rounded-md" data-testid="guardian-list">
            {guardians.map((g) => (
              <div key={g.email} className="flex items-center gap-3 p-3" data-testid={`guardian-row-${g.email}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{g.name || g.email}</p>
                  {g.name && <p className="text-xs text-muted-foreground truncate">{g.email}</p>}
                </div>
                <DeliveryBadge guardian={g} />
                {g.must_change_password && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Pending first login</span>
                )}
                <Button variant="outline" size="sm" className="text-[#00305E]" onClick={() => resendWelcome(g.email)} data-testid={`guardian-resend-${g.email}`}>Resend Welcome</Button>
                <Button variant="outline" size="sm" onClick={() => reset(g.email)} data-testid={`guardian-reset-${g.email}`}>Reset Password</Button>
                <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => remove(g.email)} data-testid={`guardian-remove-${g.email}`}>Remove</Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </CollapsibleSection>

    <CollapsibleSection id="alert-settings" title="Absence / Alert Settings" color="orange" testid="alert-settings-card">
      <div className="space-y-4">
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4" data-testid="parent-absence-alerts-row">
            <div>
              <p className="font-medium text-sm">Absence Email Alerts</p>
              <p className="text-xs text-muted-foreground">
                Automatically email guardians a warm, supportive note when this student is marked absent.
              </p>
            </div>
            <Switch checked={absenceAlerts} onCheckedChange={toggleAbsence} data-testid="parent-absence-alerts-toggle" />
          </div>

          <div className="flex items-start justify-between gap-4" data-testid="parent-pickup-alerts-row">
            <div>
              <p className="font-medium text-sm">Pickup Time Change Alerts</p>
              <p className="text-xs text-muted-foreground">
                Automatically email guardians when this student&apos;s pickup time is changed (dropoff changes are not emailed).
              </p>
            </div>
            <Switch checked={pickupAlerts} onCheckedChange={togglePickup} data-testid="parent-pickup-alerts-toggle" />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-medium text-sm">Progress Report Notification</p>
              <p className="text-xs text-muted-foreground">
                Email all guardians that a new progress report is available in the portal.
              </p>
            </div>
            <Button variant="outline" onClick={sendProgressReport} disabled={notifying || guardians.length === 0} data-testid="parent-progress-report-btn">
              {notifying ? "Sending..." : "Notify Guardians"}
            </Button>
          </div>
        </div>

        <div className="border-t pt-4" data-testid="parent-edit-history-section">
          <p className="font-medium text-sm">Parent Edit History</p>
          <p className="text-xs text-muted-foreground mb-2">
            A record of phone / address changes parents made through the portal.
          </p>
          {history.length === 0 ? (
            <p className="text-xs text-muted-foreground py-1" data-testid="parent-edit-history-empty">
              No parent edits recorded yet.
            </p>
          ) : (
            <ul className="space-y-2 max-h-64 overflow-y-auto" data-testid="parent-edit-history-list">
              {history.map((h) => (
                <li key={h.id} className="text-xs border rounded-md p-2 bg-slate-50">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{h.edited_by_name || h.edited_by_email || "Parent / Guardian"}</span>
                    <span className="text-muted-foreground">
                      {h.created_at ? new Date(h.created_at).toLocaleString("en-US", { timeZone: "America/New_York" }) : ""}
                    </span>
                  </div>
                  <ul className="mt-1 space-y-0.5">
                    {(h.changes || []).map((c, i) => (
                      <li key={`${h.id}-${i}`} className="text-muted-foreground">
                        <span className="text-slate-700 font-medium">{c.field}:</span>{" "}
                        <span className="line-through">{c.old || "—"}</span>{" → "}
                        <span className="text-slate-800">{c.new || "—"}</span>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </CollapsibleSection>
    </>
  );
}

export default GuardianAccessCard;
